import React, { useState, useEffect, useRef } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import { useNavigate } from "react-router-dom";

import logger from '../../../utils/logger';

// Import Modal component at the top with other imports
import Modal from "../../../components/shared/Modal";

const Events = () => {
  const navigate = useNavigate();
  // State for events
  const [events, setEvents] = useState([
    // Upcoming Events
    {
      id: 1,
      name: "Retirement Planning Seminar",
      date: "2024-04-15T14:00:00",
      type: "In-Person",
      subType: "Seminar",
      status: "Scheduled",
      metrics: {
        rsvps: 45,
        pendingTasks: 3,
        attendance: null,
      },
    },
    {
      id: 2,
      name: "Investment Strategy Webinar",
      date: "2024-04-20T10:00:00",
      type: "Virtual",
      subType: "External Webinar",
      status: "In Progress",
      metrics: {
        rsvps: 120,
        pendingTasks: 5,
        attendance: null,
      },
    },
    {
      id: 3,
      name: "Client Appreciation Night",
      date: "2024-05-01T18:00:00",
      type: "In-Person",
      subType: "Networking",
      status: "Scheduled",
      metrics: {
        rsvps: 75,
        pendingTasks: 8,
        attendance: null,
      },
    },
    // Past Events
    {
      id: 4,
      name: "Tax Planning Workshop",
      date: "2024-02-15T15:00:00",
      type: "In-Person",
      subType: "Educational",
      status: "Completed",
      metrics: {
        rsvps: 60,
        pendingTasks: 0,
        attendance: 52,
        feedback: 4.8,
        followUps: 15,
      },
    },
    {
      id: 5,
      name: "Market Outlook 2024",
      date: "2024-01-20T11:00:00",
      type: "Virtual",
      subType: "External Webinar",
      status: "Completed",
      metrics: {
        rsvps: 200,
        pendingTasks: 0,
        attendance: 168,
        feedback: 4.6,
        followUps: 45,
      },
    },
    {
      id: 6,
      name: "Estate Planning Basics",
      date: "2024-03-10T14:00:00",
      type: "Virtual",
      subType: "Internal Webinar",
      status: "Completed",
      metrics: {
        rsvps: 85,
        pendingTasks: 0,
        attendance: 72,
        feedback: 4.7,
        followUps: 28,
      },
    },
    {
      id: 7,
      name: "Social Security Strategies",
      date: "2024-02-28T13:00:00",
      type: "In-Person",
      subType: "Seminar",
      status: "Completed",
      metrics: {
        rsvps: 90,
        pendingTasks: 0,
        attendance: 82,
        feedback: 4.9,
        followUps: 35,
      },
    },
  ]);

  // State for filters
  const [filters, setFilters] = useState({
    type: "all",
    dateRange: "all",
    searchQuery: "",
    eventStatus: "all", // Add new filter for event status
  });

  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "asc",
  });

  // State for AI chat
  const [chatMessages, setChatMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello! I can help you plan and manage your events. What would you like to do?",
    },
  ]);
  const [userInput, setUserInput] = useState("");
  const [showEventModal, setShowEventModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [eventFormData, setEventFormData] = useState({
    // Event Details
    name: "",
    type: "",
    subType: "",
    date: "",
    time: "",
    location: "",
    description: "",
    // Banner
    bannerImage: null,
    bannerPreview: "",
    tagline: "",
    // Ticketing
    ticketType: "free",
    price: "",
    quantity: "",
    // Additional fields can be added as needed
  });

  // Add state for AI chat context
  const [chatContext, setChatContext] = useState({
    waitingForEventSelection: false,
    waitingForTopicSelection: false,
    selectedEvent: null,
    selectedTopic: null,
    showMessageDraft: false,
  });

  // Add ref for chat container auto-scroll
  const chatContainerRef = useRef(null);

  // Sample seminar events for follow-up
  const seminarEvents = [
    {
      id: 101,
      name: "Retirement Planning Seminar",
      date: "2025-03-15T14:00:00",
      location: "Phoenix, AZ",
      rsvps: 45,
      attended: 28,
      nonClients: 22,
    },
    {
      id: 102,
      name: "Investment Strategy Seminar",
      date: "2025-04-20T10:00:00",
      location: "Madison, WI",
      rsvps: 38,
      attended: 25,
      nonClients: 18,
    },
    {
      id: 103,
      name: "Estate Planning Seminar",
      date: "2025-01-10T13:00:00",
      location: "Los Angeles, CA",
      rsvps: 52,
      attended: 35,
      nonClients: 28,
    },
  ];

  // Message templates
  const messageTemplates = {
    "Annual Reviews": "Hello _______, hope you are doing well, we have year end reviews coming up and I wanted to extend an invite to review your portfolio and discuss potential year end tax optimization options for the 2025 tax year, if you are interested here is a link to my calendar.",
    "Customized by Client": "Hello _______, I hope this message finds you well. I wanted to reach out to discuss your specific financial situation and see if there are any opportunities we can explore together. Would you be interested in a personalized consultation?",
    "General": "Hello _______, I hope you're doing well. I wanted to follow up from our recent seminar and see if you have any questions or if there's anything I can help you with regarding your financial planning needs.",
    "Other": "Hello _______, thank you for attending our recent seminar. I wanted to check in and see if you'd like to discuss any specific topics or if there's anything I can assist you with.",
  };

  // Quick response suggestions for AI chat
  const quickResponses = [
    { label: "Internal Webinar", type: "Virtual" },
    { label: "External Webinar", type: "Virtual" },
    { label: "Internal Conference", type: "Virtual" },
    { label: "Seminar", type: "In-Person" },
    { label: "Networking", type: "In-Person" },
    { label: "Educational", type: "In-Person" },
  ];

  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const eventsPerPage = 5;

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleFilter = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Update handleSummaryBubbleClick
  const handleSummaryBubbleClick = (summaryType) => {
    if (summaryType === "Past Events") {
      handleFilter("eventStatus", "Completed");
    } else if (summaryType === "Upcoming Events") {
      handleFilter("eventStatus", "Scheduled");
    } else if (summaryType.includes("Virtual")) {
      handleFilter("type", "Virtual");
    } else if (summaryType.includes("In-Person")) {
      handleFilter("type", "In-Person");
    }
  };

  // Update the filtered events logic
  const filteredEvents = events.filter((event) => {
    // Filter by event status
    if (filters.eventStatus !== "all") {
      if (filters.eventStatus === "Completed" && event.status !== "Completed")
        return false;
      if (filters.eventStatus === "Scheduled" && event.status !== "Scheduled")
        return false;
      if (
        filters.eventStatus === "In Progress" &&
        event.status !== "In Progress"
      )
        return false;
    }

    // Filter by type
    if (filters.type !== "all" && event.type !== filters.type) return false;

    // Filter by search query
    if (
      filters.searchQuery &&
      !event.name.toLowerCase().includes(filters.searchQuery.toLowerCase())
    )
      return false;

    // Filter by date range
    if (filters.dateRange !== "all") {
      const eventDate = new Date(event.date);
      const now = new Date();
      const week = 7 * 24 * 60 * 60 * 1000;
      const month = 30 * 24 * 60 * 60 * 1000;
      const quarter = 3 * month;

      switch (filters.dateRange) {
        case "week":
          if (Math.abs(eventDate.getTime() - now.getTime()) > week)
            return false;
          break;
        case "month":
          if (Math.abs(eventDate.getTime() - now.getTime()) > month)
            return false;
          break;
        case "quarter":
          if (Math.abs(eventDate.getTime() - now.getTime()) > quarter)
            return false;
          break;
        default:
          break;
      }
    }

    return true;
  });

  // Calculate pagination
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(
    indexOfFirstEvent,
    indexOfLastEvent,
  );
  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle View All Events
  const handleViewAllEvents = () => {
    navigate("/advisor-portal/events/all");
  };

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!userInput.trim()) return;

    const userMessage = userInput.trim().toLowerCase();
    
    // Add user message
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userInput,
      },
    ]);

    // Handle different types of queries
    let aiResponse = "";
    
    if (userMessage.includes("no") && (userMessage.includes("that will be all") || userMessage.includes("that's all"))) {
      aiResponse = "Great! I am here if you need me.";
    } else if (userMessage.includes("seminar") && userMessage.includes("attendee")) {
      // Show event selection
      aiResponse = "Which event are you referring to? The last 3 events were March 2025 - Phoenix, AZ, April 2025 Madison, WI, and January 2025 Los Angeles, CA. Or is there another event you are referring to?";
      setChatContext(prev => ({ ...prev, waitingForEventSelection: true }));
    } else if (chatContext.waitingForEventSelection) {
      // Handle event selection
      const selectedEvent = seminarEvents.find(event => 
        userMessage.includes(event.location.toLowerCase()) || 
        userMessage.includes(event.name.toLowerCase())
      );
      
      if (selectedEvent) {
        aiResponse = `There were ${selectedEvent.rsvps} RSVPs for this event, ${selectedEvent.attended} attended, and ${selectedEvent.nonClients} are still yet to become clients. Is there a specific topic or focus you would like me to draft a text around?`;
        setChatContext(prev => ({ 
          ...prev, 
          waitingForEventSelection: false, 
          waitingForTopicSelection: true,
          selectedEvent 
        }));
      } else {
        aiResponse = "I couldn't identify which event you're referring to. Please specify: March 2025 - Phoenix, AZ, April 2025 Madison, WI, or January 2025 Los Angeles, CA.";
      }
    } else if (chatContext.waitingForTopicSelection) {
      // Handle topic selection
      const topics = ["Annual Reviews", "Customized by Client", "General", "Other"];
      const selectedTopic = topics.find(topic => 
        userMessage.includes(topic.toLowerCase().replace(" ", ""))
      );
      
      if (selectedTopic) {
        aiResponse = `I have drafted ${chatContext.selectedEvent.nonClients} messages to seminar attendees yet to become clients, navigate to your message center to review individually, review now, or send now.`;
        setChatContext(prev => ({ 
          ...prev, 
          waitingForTopicSelection: false,
          selectedTopic,
          showMessageDraft: true
        }));
      } else {
        aiResponse = "Please select a topic: Annual Reviews, Customized by Client, General, or Other.";
      }
    } else {
      // Default response for other queries
      aiResponse = `I can help you with planning a ${userInput}. Would you like to create a new event now?`;
    }

    // Simulate AI response
    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: aiResponse,
        },
      ]);
    }, 1000);

    setUserInput("");
  };

  const handleQuickResponse = (response) => {
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: `I want to create a ${response.label}`,
      },
      {
        role: "assistant",
        content: `Great choice! Let's create a ${response.label} event. I'll help you set it up.`,
      },
    ]);
    setEventFormData((prev) => ({
      ...prev,
      type: response.type,
      subType: response.label,
    }));
    setShowEventModal(true);
  };

  // Handle event selection from chat
  const handleEventSelection = (event) => {
    const userMessage = `I want to follow up with attendees from ${event.name} in ${event.location}`;
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
      },
    ]);

    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `There were ${event.rsvps} RSVPs for this event, ${event.attended} attended, and ${event.nonClients} are still yet to become clients. Is there a specific topic or focus you would like me to draft a text around?`,
        },
      ]);
    }, 1000);

    setChatContext(prev => ({ 
      ...prev, 
      waitingForEventSelection: false, 
      waitingForTopicSelection: true,
      selectedEvent: event 
    }));
  };

  // Handle topic selection from chat
  const handleTopicSelection = (topic) => {
    const userMessage = `I want to send ${topic} messages`;
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
      },
    ]);

    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `I have drafted ${chatContext.selectedEvent.nonClients} messages to seminar attendees yet to become clients, navigate to your message center to review individually, review now, or send now.`,
        },
      ]);
    }, 1000);

    setChatContext(prev => ({ 
      ...prev, 
      waitingForTopicSelection: false,
      selectedTopic: topic,
      showMessageDraft: true
    }));
  };

  // Handle review now action
  const handleReviewNow = () => {
    const userMessage = "review now";
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
      },
    ]);

    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: messageTemplates[chatContext.selectedTopic] || messageTemplates["General"],
        },
      ]);
    }, 1000);
  };

  // Handle send now action
  const handleSendNow = () => {
    const userMessage = "send now";
    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
      },
    ]);

    setTimeout(() => {
      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `✅ 22 messages have been sent to the following people:\n\n• Alexandra Rodriguez\n• Marcus Thompson\n• Isabella Chen\n• and 19 others\n\nIs there anything else I can help you with today?`,
        },
      ]);
    }, 1000);

    // Reset chat context
    setChatContext(prev => ({ 
      ...prev, 
      waitingForEventSelection: false,
      waitingForTopicSelection: false,
      selectedEvent: null,
      selectedTopic: null,
      showMessageDraft: false
    }));
  };

  // Event summary data
  const eventSummaries = [
    {
      title: "Upcoming Events",
      count: events.filter((e) => new Date(e.date) > new Date()).length,
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      gradient: "from-blue-500/10 to-blue-600/10",
      color: "text-blue-700",
      borderColor: "border-blue-200",
    },
    {
      title: "Past Events",
      count: events.filter((e) => new Date(e.date) < new Date()).length,
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      gradient: "from-purple-500/10 to-purple-600/10",
      color: "text-purple-700",
      borderColor: "border-purple-200",
    },
    {
      title: "Virtual Events",
      count: events.filter((e) => e.type === "Virtual").length,
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      gradient: "from-emerald-500/10 to-emerald-600/10",
      color: "text-emerald-700",
      borderColor: "border-emerald-200",
    },
    {
      title: "In-Person Events",
      count: events.filter((e) => e.type === "In-Person").length,
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      gradient: "from-amber-500/10 to-amber-600/10",
      color: "text-amber-700",
      borderColor: "border-amber-200",
    },
  ];

  // Get current event if any
  const getCurrentEvent = () => {
    const now = new Date();
    return events.find((event) => {
      const eventDate = new Date(event.date);
      return (
        eventDate <= now &&
        eventDate.getTime() + 2 * 60 * 60 * 1000 > now.getTime()
      ); // 2 hours duration
    });
  };

  const currentEvent = getCurrentEvent();

  const steps = [
    {
      number: 1,
      title: "Event Details",
      description: "Basic information about your event",
    },
    { number: 2, title: "Banner", description: "Add visuals and description" },
    {
      number: 3,
      title: "Ticketing",
      description: "Set up registration options",
    },
    { number: 4, title: "Review", description: "Review and publish" },
  ];

  const handleInputChange = (field, value) => {
    setEventFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStepChange = (direction) => {
    if (direction === "next" && currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
    } else if (direction === "back" && currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleInputChange("bannerImage", file);
        handleInputChange("bannerPreview", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveAsDraft = () => {
    // Save form data as draft
    logger.info("Saving as draft:", eventFormData);
    setShowEventModal(false);
  };

  const handlePublishEvent = () => {
    // Validate and publish event
    logger.info("Publishing event:", eventFormData);
    setShowEventModal(false);
  };

  const handleViewDetails = (event) => {
    // Use React Router navigation instead of window.location
    navigate(`/advisor-portal/events/${event.id}`);
  };

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Events", path: "/advisor-portal/events" },
      ]}
      title="Events"
      subtitle="Plan and manage your events"
    >
      <div className="space-y-8">
        {/* Event Summary Bubbles */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {eventSummaries.map((summary, index) => (
            <div
              key={index}
              onClick={() => handleSummaryBubbleClick(summary.title)}
              className={`bg-gradient-to-br ${summary.gradient} border ${summary.borderColor} rounded-2xl p-6 
                transition-all duration-200 hover:scale-[1.02] hover:shadow-lg dark:shadow-none cursor-pointer 
                group relative overflow-hidden`}
            >
              <div className="absolute inset-0 bg-surface-default dark:bg-surface-default/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              <div className="relative">
                <div className="flex items-start justify-between mb-4">
                  <div className={`${summary.color}`}>{summary.icon}</div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEventFormData((prev) => ({
                        ...prev,
                        type: summary.title.includes("Virtual")
                          ? "Virtual"
                          : "In-Person",
                      }));
                      setShowEventModal(true);
                    }}
                    className={`${summary.color} bg-surface-default dark:bg-surface-default/80 hover:bg-surface-default dark:bg-surface-default px-4 py-2 rounded-xl text-sm font-medium 
                      shadow-sm hover:shadow transition-all duration-200`}
                  >
                    Create
                  </button>
                </div>
                <h3 className={`text-lg font-semibold ${summary.color} mb-2`}>
                  {summary.title}
                </h3>
                <p className={`text-2xl font-bold ${summary.color}`}>
                  {summary.count}
                  <span className="text-sm font-normal ml-1">Events</span>
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Current Event Banner */}
        {currentEvent && (
          <div
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl p-8 shadow-lg dark:shadow-none
            relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-surface-default dark:bg-surface-default/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            <div className="relative flex justify-between items-start">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <span className="px-3 py-1 text-sm bg-surface-default dark:bg-surface-default/20 rounded-full font-medium animate-pulse">
                      Live Now
                    </span>
                    <h3 className="text-2xl font-bold">{currentEvent.name}</h3>
                  </div>
                  <p className="text-white/80">
                    Started at{" "}
                    {new Date(currentEvent.date).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5 text-white/80"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                    <span className="text-sm">
                      {currentEvent.metrics.rsvps} Attendees
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-5 h-5 text-white/80"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      />
                    </svg>
                    <span className="text-sm">
                      {currentEvent.metrics.pendingTasks} Tasks Pending
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  className="px-6 py-3 bg-surface-default dark:bg-surface-default text-blue-600 rounded-xl font-medium hover:bg-blue-50 
                  transition-colors shadow-lg dark:shadow-none hover:shadow-xl"
                >
                  {currentEvent.type === "Virtual"
                    ? "Join Now"
                    : "View Details"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="grid grid-cols-3 gap-6">
          {/* AI Event Planner */}
          <div className="col-span-2">
            <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden">
              <div className="p-6 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        AI Event Assistant
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        Get help planning and managing your events
                      </p>
                    </div>
                  </div>
                  <button
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title="Minimize"
                  >
                    <svg
                      className="w-5 h-5 text-gray-500 dark:text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Chat Messages */}
              <div 
                ref={chatContainerRef}
                className="h-[400px] p-6 overflow-y-auto space-y-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
              >
                {chatMessages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {message.role === "assistant" && (
                      <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center mr-3 mt-1">
                        <svg
                          className="w-5 h-5 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                          />
                        </svg>
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.role === "user"
                          ? "bg-blue-600 text-white ml-4"
                          : "bg-gray-100 text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {message.content}
                    </div>
                    {message.role === "user" && (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center ml-3 mt-1">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="p-6 border-t border-gray-100 dark:border-gray-800 space-y-4">
                {/* Quick Response Buttons */}
                <div className="flex flex-wrap gap-2">
                  {quickResponses.map((response, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickResponse(response)}
                      className="px-4 py-2 text-sm bg-gray-100 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 
                        transition-colors flex items-center space-x-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d={
                            response.type === "Virtual"
                              ? "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                              : "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          }
                        />
                      </svg>
                      <span>{response.label}</span>
                    </button>
                  ))}
                </div>

                {/* Event Selection Buttons */}
                {chatContext.waitingForEventSelection && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Select an event:</p>
                    <div className="flex flex-wrap gap-2">
                      {seminarEvents.map((event, index) => (
                        <button
                          key={index}
                          onClick={() => handleEventSelection(event)}
                          className="px-4 py-2 text-sm bg-blue-100 text-blue-700 dark:text-blue-300 rounded-xl hover:bg-blue-200 
                            transition-colors flex items-center space-x-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span>{event.name} - {event.location}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Topic Selection Buttons */}
                {chatContext.waitingForTopicSelection && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Select a topic:</p>
                    <div className="flex flex-wrap gap-2">
                      {["Annual Reviews", "Customized by Client", "General", "Other"].map((topic, index) => (
                        <button
                          key={index}
                          onClick={() => handleTopicSelection(topic)}
                          className="px-4 py-2 text-sm bg-green-100 text-green-700 dark:text-green-300 rounded-xl hover:bg-green-200 
                            transition-colors flex items-center space-x-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                          </svg>
                          <span>{topic}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {chatContext.showMessageDraft && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Choose an action:</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleReviewNow}
                        className="px-4 py-2 text-sm bg-purple-100 text-purple-700 dark:text-purple-300 rounded-xl hover:bg-purple-200 
                          transition-colors flex items-center space-x-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        <span>Review Now</span>
                      </button>
                      <button
                        onClick={handleSendNow}
                        className="px-4 py-2 text-sm bg-orange-100 text-orange-700 dark:text-orange-300 rounded-xl hover:bg-orange-200 
                          transition-colors flex items-center space-x-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                          />
                        </svg>
                        <span>Send Now</span>
                      </button>
                      <button
                        className="px-4 py-2 text-sm bg-gray-100 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 
                          transition-colors flex items-center space-x-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10"
                          />
                        </svg>
                        <span>Message Center</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Chat Input */}
                <form onSubmit={handleChatSubmit} className="flex gap-3">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      placeholder="Ask me anything about event planning..."
                      className="w-full px-4 py-3 bg-gray-100 border-0 rounded-xl focus:outline-none focus:ring-2 
                        focus:ring-blue-500 focus:bg-surface-default dark:bg-surface-default transition-all pr-12"
                    />
                    <button
                      type="submit"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-400 
                        hover:text-blue-600 transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 5l7 7-7 7M5 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </form>

                {/* Suggested Questions */}
                <div className="text-sm text-gray-500 dark:text-gray-500">
                  <span className="font-medium">Try asking:</span>
                  <div className="mt-2 space-y-1">
                    <button className="block text-left hover:text-blue-600 transition-colors">
                      How do I create a recurring event?
                    </button>
                    <button className="block text-left hover:text-blue-600 transition-colors">
                      What is the best way to promote my event?
                    </button>
                    <button className="block text-left hover:text-blue-600 transition-colors">
                      How can I track event attendance?
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions Panel */}
          <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Quick Actions
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => setShowEventModal(true)}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 
                  transition-colors flex items-center justify-center space-x-2 shadow-sm"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <span>Create New Event</span>
              </button>

              <button
                onClick={handleViewAllEvents}
                className="w-full px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 rounded-xl hover:bg-gray-200 
                  transition-colors flex items-center justify-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 10h16M4 14h16M4 18h16"
                  />
                </svg>
                <span>View All Events</span>
              </button>

              <button
                className="w-full px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 rounded-xl hover:bg-gray-200 
                transition-colors flex items-center justify-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                  />
                </svg>
                <span>Manage Templates</span>
              </button>

              <button
                className="w-full px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 rounded-xl hover:bg-gray-200 
                transition-colors flex items-center justify-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <span>Reports & Analytics</span>
              </button>
            </div>
          </div>
        </div>

        {/* Events List */}
        <div className="bg-surface-default dark:bg-surface-default rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6">
          <div className="flex flex-col space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {filters.eventStatus === "Completed"
                  ? "Past Events"
                  : "Upcoming Events"}
              </h2>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative">
                  <select
                    value={filters.type}
                    onChange={(e) => handleFilter("type", e.target.value)}
                    className="appearance-none pl-8 pr-10 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                      focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="all">All Types</option>
                    <option value="Virtual">Virtual</option>
                    <option value="In-Person">In-Person</option>
                  </select>
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>

                <div className="relative">
                  <select
                    value={filters.dateRange}
                    onChange={(e) => handleFilter("dateRange", e.target.value)}
                    className="appearance-none pl-8 pr-10 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                      focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="all">All Dates</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="quarter">This Quarter</option>
                  </select>
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="relative flex-1 md:max-w-xs">
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={filters.searchQuery}
                    onChange={(e) =>
                      handleFilter("searchQuery", e.target.value)
                    }
                    className="w-full pl-8 pr-4 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:bg-surface-default transition-colors"
                  />
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="relative">
                  <select
                    value={filters.eventStatus}
                    onChange={(e) =>
                      handleFilter("eventStatus", e.target.value)
                    }
                    className="appearance-none pl-8 pr-10 py-2 bg-gray-100 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300
                      focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value="all">All Status</option>
                    <option value="Scheduled">Scheduled</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                  <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  </div>
                </div>

                {Object.values(filters).some(
                  (value) => value !== "all" && value !== "",
                ) && (
                  <button
                    onClick={() =>
                      setFilters({
                        type: "all",
                        dateRange: "all",
                        searchQuery: "",
                      })
                    }
                    className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Events Grid */}
            <div className="grid grid-cols-1 gap-4">
              {currentEvents.map((event) => (
                <div
                  key={event.id}
                  className="group border border-gray-200 dark:border-gray-700 rounded-2xl p-6 hover:shadow-lg dark:shadow-none transition-all duration-200
                    hover:border-blue-200 relative overflow-hidden"
                >
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-blue-50/0 via-blue-50/0 to-blue-50 opacity-0 
                    group-hover:opacity-100 transition-opacity duration-200"
                  />
                  <div className="relative">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <h3
                            className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 
                            transition-colors"
                          >
                            {event.name}
                          </h3>
                          <div className="flex gap-2">
                            <span
                              className={`px-3 py-1 text-sm rounded-full font-medium ${
                                event.type === "Virtual"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-emerald-100 text-emerald-800"
                              }`}
                            >
                              {event.type}
                            </span>
                            <span
                              className={`px-3 py-1 text-sm rounded-full font-medium ${
                                event.status === "Scheduled"
                                  ? "bg-amber-100 text-amber-800"
                                  : event.status === "In Progress"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : "bg-gray-100 text-gray-800 dark:text-gray-200"
                              }`}
                            >
                              {event.status}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {new Date(event.date).toLocaleDateString("en-US", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-500">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-blue-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                              </svg>
                            </div>
                            <span>{event.metrics.rsvps} RSVPs</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-blue-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                                />
                              </svg>
                            </div>
                            <span>{event.metrics.pendingTasks} Tasks</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleViewDetails(event)}
                          className="px-4 py-2 text-blue-600 hover:text-blue-700 text-sm font-medium 
                            hover:bg-blue-50 rounded-xl transition-colors"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                    {event.status === "Completed" && (
                      <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-green-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-500">
                            {event.metrics.attendance} Attended
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-yellow-50 flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-yellow-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-500">
                            {event.metrics.feedback} / 5.0
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-purple-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                              />
                            </svg>
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-500">
                            {event.metrics.followUps} Follow-ups
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add Pagination */}
            {filteredEvents.length > eventsPerPage && (
              <div className="flex justify-center items-center space-x-2 mt-6">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => handlePageChange(index + 1)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      currentPage === index + 1
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event Creation Modal */}
      {showEventModal && (
        <Modal 
          isOpen={showEventModal}
          onClose={() => setShowEventModal(false)}
          maxWidth="4xl"
        >
          <div className="flex flex-col min-h-[600px]">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    Create New Event
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    Fill in the details to create your event
                  </p>
                </div>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  <svg
                    className="w-6 h-6 text-gray-500 dark:text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Stepper */}
              <div className="flex items-center justify-between mt-8">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex items-center">
                    <div className="relative">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 
                        ${
                          currentStep > step.number
                            ? "bg-blue-600 border-blue-600 text-white"
                            : currentStep === step.number
                              ? "border-blue-600 text-blue-600"
                              : "border-gray-300 text-gray-300"
                        }`}
                      >
                        {currentStep > step.number ? (
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          step.number
                        )}
                      </div>
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
                        <p
                          className={`text-sm font-medium ${
                            currentStep >= step.number
                              ? "text-blue-600"
                              : "text-gray-400"
                          }`}
                        >
                          {step.title}
                        </p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-full h-0.5 mx-4 ${
                          currentStep > step.number
                            ? "bg-blue-600"
                            : "bg-gray-300"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              {currentStep === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Event Name
                    </label>
                    <input
                      type="text"
                      value={eventFormData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      placeholder="Enter event name"
                      className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Event Type
                      </label>
                      <select
                        value={eventFormData.type}
                        onChange={(e) => handleInputChange("type", e.target.value)}
                        className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                          focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      >
                        <option value="">Select Type</option>
                        <option value="Virtual">Virtual</option>
                        <option value="In-Person">In-Person</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sub-Type
                      </label>
                      <select
                        value={eventFormData.subType}
                        onChange={(e) => handleInputChange("subType", e.target.value)}
                        className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                          focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      >
                        <option value="">Select Sub-Type</option>
                        <option value="Internal Webinar">Internal Webinar</option>
                        <option value="External Webinar">External Webinar</option>
                        <option value="Internal Conference">Internal Conference</option>
                        <option value="Seminar">Seminar</option>
                        <option value="Networking">Networking</option>
                        <option value="Educational">Educational</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        value={eventFormData.date}
                        onChange={(e) => handleInputChange("date", e.target.value)}
                        className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Time
                      </label>
                      <input
                        type="time"
                        value={eventFormData.time}
                        onChange={(e) => handleInputChange("time", e.target.value)}
                        className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {eventFormData.type === "Virtual" ? "Event URL" : "Location"}
                    </label>
                    <input
                      type="text"
                      value={eventFormData.location}
                      onChange={(e) => handleInputChange("location", e.target.value)}
                      placeholder={eventFormData.type === "Virtual" ? "Enter event URL" : "Enter location"}
                      className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={eventFormData.description}
                      onChange={(e) => handleInputChange("description", e.target.value)}
                      placeholder="Enter event description"
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                    />
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Event Banner
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-700 border-dashed rounded-xl">
                      <div className="space-y-1 text-center">
                        {eventFormData.bannerPreview ? (
                          <div className="relative">
                            <img
                              src={eventFormData.bannerPreview}
                              alt="Banner preview"
                              className="max-h-48 rounded-lg mx-auto"
                            />
                            <button
                              onClick={() => {
                                handleInputChange("bannerImage", null);
                                handleInputChange("bannerPreview", "");
                              }}
                              className="absolute top-2 right-2 p-1 bg-surface-default dark:bg-surface-default rounded-full shadow-lg dark:shadow-none
                                hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                              <svg
                                className="w-5 h-5 text-gray-600 dark:text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <>
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                            </svg>
                            <div className="flex text-sm text-gray-600 dark:text-gray-400">
                              <label
                                htmlFor="file-upload"
                                className="relative cursor-pointer bg-surface-default dark:bg-surface-default rounded-md font-medium text-blue-600 hover:text-blue-500"
                              >
                                <span>Upload a file</span>
                                <input
                                  id="file-upload"
                                  name="file-upload"
                                  type="file"
                                  className="sr-only"
                                  accept="image/*"
                                  onChange={handleImageUpload}
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-500">
                              PNG, JPG, GIF up to 10MB
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Event Tagline
                      <span className="text-gray-400 ml-1">
                        ({140 - (eventFormData.tagline?.length || 0)} characters remaining)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={eventFormData.tagline}
                      onChange={(e) => handleInputChange("tagline", e.target.value)}
                      maxLength={140}
                      placeholder="Enter a short, catchy tagline for your event"
                      className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                    />
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ticket Type
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => handleInputChange("ticketType", "free")}
                        className={`px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-center space-x-2
                          ${eventFormData.ticketType === "free"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                          } transition-colors`}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <span>Free Event</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleInputChange("ticketType", "paid")}
                        className={`px-4 py-3 rounded-xl text-sm font-medium flex items-center justify-center space-x-2
                          ${eventFormData.ticketType === "paid"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                          } transition-colors`}
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span>Paid Event</span>
                      </button>
                    </div>
                  </div>

                  {eventFormData.ticketType === "paid" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ticket Price
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 dark:text-gray-500 sm:text-sm">$</span>
                        </div>
                        <input
                          type="number"
                          value={eventFormData.price}
                          onChange={(e) => handleInputChange("price", e.target.value)}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          className="w-full pl-7 pr-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Available Spots
                    </label>
                    <input
                      type="number"
                      value={eventFormData.quantity}
                      onChange={(e) => handleInputChange("quantity", e.target.value)}
                      placeholder="Enter number of available spots"
                      min="1"
                      className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border-0 rounded-xl text-sm text-gray-900 dark:text-gray-100
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-surface-default dark:focus:bg-surface-default transition-colors"
                    />
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-8">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Event Details
                    </h3>
                    <dl className="grid grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Event Name
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                          {eventFormData.name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Event Type
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                          {eventFormData.type}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Sub-Type
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                          {eventFormData.subType}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Date & Time
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                          {eventFormData.date} at {eventFormData.time}
                        </dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Location
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                          {eventFormData.location}
                        </dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Description
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                          {eventFormData.description}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Banner & Tagline
                    </h3>
                    {eventFormData.bannerPreview && (
                      <img
                        src={eventFormData.bannerPreview}
                        alt="Event banner"
                        className="w-full h-32 object-cover rounded-lg mb-4"
                      />
                    )}
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {eventFormData.tagline}
                    </p>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                      Ticketing
                    </h3>
                    <dl className="grid grid-cols-2 gap-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Ticket Type
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 capitalize">
                          {eventFormData.ticketType}
                        </dd>
                      </div>
                      {eventFormData.ticketType === "paid" && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                            Price
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                            ${eventFormData.price}
                          </dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-500">
                          Available Spots
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                          {eventFormData.quantity}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-100 dark:border-gray-800">
              <div className="flex justify-between items-center">
                <button
                  onClick={handleSaveAsDraft}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 text-sm font-medium transition-colors"
                >
                  Save as Draft
                </button>
                <div className="flex items-center gap-3">
                  {currentStep > 1 && (
                    <button
                      onClick={() => handleStepChange("back")}
                      className="px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium
                        hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      Back
                    </button>
                  )}
                  {currentStep < steps.length ? (
                    <button
                      onClick={() => handleStepChange("next")}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium
                        hover:bg-blue-700 transition-colors"
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      onClick={handlePublishEvent}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium
                        hover:bg-blue-700 transition-colors"
                    >
                      Publish Event
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </SubPageLayout>
  );
};

export default Events;
